import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, MailOpen } from "lucide-react";
import type {
  AccountDto,
  FilterDto,
  LabelMapDto,
  MessageSummaryDto,
  Recipient,
  SenderFilterDto,
  VirtualFolderDto,
} from "../../../shared/types";
import {
  useAllFolderMessages,
  useDeleteMessage,
  useMarkRead,
  useInfiniteMessages,
  useSenderGroups,
  useThread,
  useVirtualFolders,
} from "../../hooks/useApi";

/** Short stamp for thread rows: "22. jun. 14:03" style, year added when it differs. */
function threadWhen(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (date.getFullYear() === now.getFullYear()) {
    return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
  }
  return `${date.toLocaleDateString()} ${time}`;
}

/** Expanded conversation under a message row: all stored mail sharing its subject. */
function ThreadView({
  accountId,
  messageId,
  selectedId,
  onSelect,
}: {
  accountId: number;
  messageId: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const { data, error, isLoading } = useThread(accountId, messageId);

  if (isLoading) {
    return (
      <p className="border-b border-stroke bg-surface px-4 py-2 pl-12 text-[12px] text-ink-muted">
        Loading conversation…
      </p>
    );
  }
  if (error) return <ErrorState error={error} compact />;

  // Newest answer on top, like Outlook's conversation view.
  const siblings = [...(data?.messages ?? [])].reverse();
  if (siblings.length <= 1) {
    return (
      <p className="border-b border-stroke bg-surface px-4 py-2 pl-12 text-[12px] text-ink-muted">
        No other messages in this conversation.
      </p>
    );
  }

  return (
    <div className="border-b border-stroke bg-surface">
      {siblings.map((m) => {
        const sent = m.folderKind === "sent";
        const current = m.id === selectedId || m.id === messageId;
        return (
          <button
            key={m.id}
            onClick={() => onSelect(m.id)}
            className={`flex w-full items-baseline gap-2 border-b border-stroke/60 py-1.5 pl-12 pr-3 text-left text-[12px] last:border-b-0 hover:bg-black/[.04] ${
              current ? "bg-accent-subtle" : ""
            }`}
          >
            <span
              className={`shrink-0 rounded px-1.5 py-px text-[10px] font-semibold uppercase ${
                sent ? "bg-accent-subtle text-accent" : "bg-black/5 text-ink-muted"
              }`}
            >
              {sent ? "Sent" : m.folderKind === "inbox" ? "Inbox" : m.folderKind ?? ""}
            </span>
            <span className={`shrink-0 truncate ${m.isRead ? "" : "font-semibold"}`}>
              {sent ? "Me" : m.from?.name ?? m.from?.address ?? "Unknown"}
            </span>
            <span className="min-w-0 flex-1 truncate text-ink-muted">{m.bodyPreview}</span>
            <span className="shrink-0 text-[11px] text-ink-muted">
              {threadWhen(m.receivedDateTime)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
import { MessageListItem, type RowAvatar } from "./MessageListItem";
import { ErrorState } from "../common/ErrorState";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { providerMeta } from "../providerMeta";
import { OTHER_FILTER_ID, SWATCH_CLASSES } from "../filterColors";

interface MessageListProps {
  accountId: number;
  folderId: string | null;
  selectedId: string | null;
  /** The event lets the parent handle shift+click range selection. */
  onSelect: (id: string, e?: React.MouseEvent) => void;
  labels: LabelMapDto;
  filters: FilterDto[];
  accounts: AccountDto[];
  /** null = All; a filter id; or OTHER_FILTER_ID for messages with no label. */
  selectedFilter: number | null;
  /** Combined view only: accounts whose messages are toggled off. */
  hiddenAccountIds?: Set<number>;
  /** Show only unread messages (funnel toggle in the toolbar). */
  unreadOnly?: boolean;
  /** Called after a message was deleted (e.g. to clear the reading pane). */
  onDeleted?: (messageId: string) => void;
  /** Open the create-filter dialog pre-filled with a row's sender. */
  onCreateFilterFromSender?: (from: Recipient) => void;
  /** Pin a row's email to an AI group. */
  onAddToAiGroup?: (message: MessageSummaryDto) => void;
  /** Multi-select mode: show checkboxes on every row. */
  selectMode?: boolean;
  checkedIds?: Set<string>;
  onToggleChecked?: (messageId: string) => void;
}

const GROUP_ORDER = ["Today", "Yesterday", "Older"];

/**
 * Default collapse per group: Today is always open; Yesterday stays open too
 * while Today is thin (< 30 mails) so the list never looks empty; Older is
 * collapsed until the user opens it.
 */
function collapsedByDefault(key: string, todayCount: number): boolean {
  if (key === "Older") return true;
  if (key === "Yesterday") return todayCount >= 30;
  return false;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function groupKey(iso: string, now: Date): string {
  const date = new Date(iso);
  const dayDiff = Math.floor((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (dayDiff <= 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  return "Older";
}

/** Account-number avatar for a message (origin in All view, own account otherwise). */
export function avatarFor(
  message: MessageSummaryDto,
  accounts: AccountDto[],
  accountId: number,
): RowAvatar | undefined {
  const id = message.origin?.accountId ?? accountId;
  const index = accounts.findIndex((a) => a.id === id);
  if (index < 0) return undefined;
  const account = accounts[index]!;
  return { number: index + 1, color: account.color, username: account.username };
}

export function MessageList({
  accountId,
  folderId,
  selectedId,
  onSelect,
  labels,
  filters,
  accounts,
  selectedFilter,
  hiddenAccountIds,
  unreadOnly,
  onDeleted,
  onCreateFilterFromSender,
  onAddToAiGroup,
  selectMode,
  checkedIds,
  onToggleChecked,
}: MessageListProps) {
  const { data, error, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteMessages(accountId, folderId);
  const deleteMessage = useDeleteMessage(accountId);
  const markRead = useMarkRead(accountId);
  // Combined view only: folder sub-panels inside the date groups.
  const { data: virtualFolders } = useVirtualFolders(accountId === 0);
  const folderLists = useAllFolderMessages(
    (virtualFolders ?? []).map((f) => f.id),
    accountId === 0,
  );
  // sender address (lowercased) → sender filters that list it, for row marks.
  const { data: senderGroups } = useSenderGroups();
  const senderFiltersByAddress = new Map<string, SenderFilterDto[]>();
  for (const group of senderGroups ?? []) {
    for (const filter of group.filters) {
      for (const address of filter.addresses) {
        const key = address.toLowerCase();
        let list = senderFiltersByAddress.get(key);
        if (!list) senderFiltersByAddress.set(key, (list = []));
        list.push(filter);
      }
    }
  }
  // User toggles override the data-driven defaults (see collapsedByDefault).
  const [collapseOverrides, setCollapseOverrides] = useState<Map<string, boolean>>(new Map());
  // Re-render when the calendar day flips, so "Today"/"Yesterday" regroup at
  // midnight even if no new data arrives (structural sharing skips renders
  // when refetches return identical mail).
  const [dayStamp, setDayStamp] = useState(() => new Date().toDateString());
  useEffect(() => {
    const timer = setInterval(() => {
      const stamp = new Date().toDateString();
      setDayStamp((prev) => (prev === stamp ? prev : stamp));
    }, 60_000);
    return () => clearInterval(timer);
  }, []);
  void dayStamp; // read only for the re-render it triggers
  const [pendingDelete, setPendingDelete] = useState<MessageSummaryDto | null>(null);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [reconnect, setReconnect] = useState<{ url: string; username: string } | null>(null);

  if (isLoading) {
    return <p className="px-4 py-3 text-[13px] text-ink-muted">Loading messages…</p>;
  }
  if (error) return <ErrorState error={error} compact />;

  const runDelete = () => {
    const m = pendingDelete;
    if (!m) return;
    // Optimistic: the hook prunes the row immediately — close the dialog and
    // clear the reading pane now; a failed delete restores the row and shows
    // the existing error strip below the list.
    setPendingDelete(null);
    onDeleted?.(m.id);
    deleteMessage.mutate(m.id, {
      onError: (err) => {
        if (err.name === "ReauthError") {
          const account = accounts.find((a) => a.id === (m.origin?.accountId ?? accountId));
          if (account) {
            const base = providerMeta(account.provider).loginUrl;
            setReconnect({
              url: base ? `${base}?hint=${encodeURIComponent(account.username)}` : "",
              username: account.username,
            });
          }
        }
      },
    });
  };

  const toggleGroup = (key: string, currentlyCollapsed: boolean) => {
    setCollapseOverrides((prev) => new Map(prev).set(key, !currentlyCollapsed));
  };

  // The same narrowing applies to inbox and folder mail (unread funnel,
  // hidden accounts, AI-group filter).
  const passes = (m: MessageSummaryDto) => {
    if (m.origin && hiddenAccountIds?.has(m.origin.accountId)) return false;
    if (unreadOnly && m.isRead) return false;
    if (selectedFilter === null) return true; // All
    const matched = labels[m.id] ?? [];
    if (selectedFilter === OTHER_FILTER_ID) return matched.length === 0;
    return matched.includes(selectedFilter);
  };

  const messages = data?.pages.flatMap((p) => p.messages) ?? [];
  const visible = messages.filter(passes);

  // Bucket the (date-desc) list into ordered groups.
  const now = new Date();
  const groups = new Map<string, MessageSummaryDto[]>();
  for (const m of visible) {
    const key = groupKey(m.receivedDateTime, now);
    let list = groups.get(key);
    if (!list) groups.set(key, (list = []));
    list.push(m);
  }

  // Combined view: bucket every folder's mail by day too — each date group
  // shows an "Inbox" sub-panel plus one per folder with mail that day.
  const folderByDay = new Map<string, { folder: VirtualFolderDto; messages: MessageSummaryDto[] }[]>();
  if (accountId === 0) {
    for (const { folderId, messages: folderMail } of folderLists) {
      const folder = (virtualFolders ?? []).find((f) => f.id === folderId);
      if (!folder) continue;
      const perDay = new Map<string, MessageSummaryDto[]>();
      for (const m of folderMail) {
        if (!passes(m)) continue;
        const key = groupKey(m.receivedDateTime, now);
        let list = perDay.get(key);
        if (!list) perDay.set(key, (list = []));
        list.push(m);
      }
      for (const [key, list] of perDay) {
        let panels = folderByDay.get(key);
        if (!panels) folderByDay.set(key, (panels = []));
        panels.push({ folder, messages: list });
      }
    }
    // Busiest folder of the day first.
    for (const panels of folderByDay.values()) {
      panels.sort((a, b) => b.messages.length - a.messages.length);
    }
  }
  const dayTotal = (key: string) =>
    (groups.get(key)?.length ?? 0) +
    (folderByDay.get(key) ?? []).reduce((n, p) => n + p.messages.length, 0);

  // The sub-panel envelope clears the WHOLE folder (all days), so the sidebar
  // badge drops to zero — day-scoped marking left older unread behind and
  // made the badge look stuck.
  const folderUnread = new Map<number, MessageSummaryDto[]>();
  for (const { folderId, messages: folderMail } of folderLists) {
    folderUnread.set(
      folderId,
      folderMail.filter((m) => passes(m) && !m.isRead),
    );
  }
  const inboxUnread = visible.filter((m) => !m.isRead);

  const skipped = data?.pages[0]?.skipped ?? [];

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {skipped.length > 0 && (
        <p className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-[12px] text-amber-800">
          Couldn't load {skipped.length} account{skipped.length === 1 ? "" : "s"}: {skipped.join(", ")}
        </p>
      )}
      {messages.length === 0 && folderByDay.size === 0 && (
        <p className="px-4 py-6 text-center text-[13px] text-ink-muted">Inbox is empty.</p>
      )}
      {GROUP_ORDER.filter(
        // "Today" always renders — on a new day it appears (empty) before the
        // first mail arrives instead of the list starting at "Yesterday".
        (key) => key === "Today" || groups.has(key) || folderByDay.has(key),
      ).map((key) => {
        const collapsed =
          collapseOverrides.get(key) ?? collapsedByDefault(key, dayTotal("Today"));
        const inboxList = groups.get(key) ?? [];
        const panels = folderByDay.get(key) ?? [];

        const renderRows = (list: MessageSummaryDto[]) =>
          list.map((message) => (
            <div key={message.id}>
              <MessageListItem
                message={message}
                selected={message.id === selectedId}
                matchedFilters={(labels[message.id] ?? [])
                  .map((id) => filters.find((f) => f.id === id))
                  .filter((f): f is FilterDto => f !== undefined)}
                senderMarks={
                  message.from
                    ? senderFiltersByAddress.get(message.from.address.toLowerCase())
                    : undefined
                }
                avatar={avatarFor(message, accounts, accountId)}
                onClick={(e) => onSelect(message.id, e)}
                onDelete={() => setPendingDelete(message)}
                onMarkRead={() => markRead.mutate(message.id)}
                onCreateFilterFromSender={onCreateFilterFromSender}
                onAddToAiGroup={onAddToAiGroup}
                onToggleThread={(m) =>
                  setExpandedThreadId((prev) => (prev === m.id ? null : m.id))
                }
                threadExpanded={expandedThreadId === message.id}
                dragAccountId={accountId}
                dragIds={
                  selectMode && checkedIds?.has(message.id) ? [...checkedIds] : undefined
                }
                selectMode={selectMode}
                checked={checkedIds?.has(message.id) ?? false}
                onToggleChecked={() => onToggleChecked?.(message.id)}
              />
              {expandedThreadId === message.id && (
                <ThreadView
                  accountId={accountId}
                  messageId={message.id}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              )}
            </div>
          ));

        // Second accordion level: "Inbox" + per-folder panels for this day.
        const renderSubPanel = (
          panelKey: string,
          label: string,
          list: MessageSummaryDto[],
          allUnread: MessageSummaryDto[],
          dotColor?: string,
          defaultCollapsed = true,
        ) => {
          const subCollapsed = collapseOverrides.get(panelKey) ?? defaultCollapsed;
          const dayUnread = list.filter((m) => !m.isRead);
          return (
            <div key={panelKey}>
              <button
                onClick={() => toggleGroup(panelKey, subCollapsed)}
                className="flex w-full items-center gap-1.5 border-b border-stroke bg-black/[.07] py-1.5 pl-6 pr-3 text-left text-[12px] font-medium text-ink hover:bg-black/10"
              >
                {subCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                {dotColor && (
                  <span className={`size-2 shrink-0 rounded-full ${SWATCH_CLASSES[dotColor as keyof typeof SWATCH_CLASSES] ?? SWATCH_CLASSES.gray}`} />
                )}
                {label}
                {/* This day's unread, like the sidebar badges — gone once read. */}
                {dayUnread.length > 0 && (
                  <span className="font-semibold text-accent">{dayUnread.length}</span>
                )}
                {allUnread.length > 0 && (
                  <span
                    role="button"
                    aria-label={`Mark all ${allUnread.length} unread in ${label} as read`}
                    title={`Mark all ${allUnread.length} unread in ${label} as read`}
                    onClick={(e) => {
                      e.stopPropagation();
                      for (const m of allUnread) markRead.mutate(m.id);
                    }}
                    className="ml-auto grid size-5 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-accent"
                  >
                    <MailOpen size={13} />
                  </span>
                )}
              </button>
              {!subCollapsed && renderRows(list)}
            </div>
          );
        };

        return (
          <div key={key}>
            <button
              onClick={() => toggleGroup(key, collapsed)}
              className="flex w-full items-center gap-1.5 border-b border-stroke bg-black/15 px-3 py-1.5 text-left text-[12px] font-semibold text-ink hover:bg-black/20"
            >
              {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
              {key}
              <span className="font-normal text-ink-muted">{dayTotal(key)}</span>
            </button>
            {!collapsed && inboxList.length === 0 && panels.length === 0 && (
              <p className="border-b border-stroke px-4 py-2 text-[12px] text-ink-muted">
                No mail yet today.
              </p>
            )}
            {/* Single-account view keeps the flat list — folders are user-level
                and would mix mail from other accounts into this tab. */}
            {!collapsed && accountId !== 0 && renderRows(inboxList)}
            {!collapsed && accountId === 0 && (
              <>
                {inboxList.length > 0 &&
                  renderSubPanel(
                    `${key}/inbox`,
                    "Inbox",
                    inboxList,
                    inboxUnread,
                    undefined,
                    key !== "Today",
                  )}
                {panels.map((p) =>
                  renderSubPanel(
                    `${key}/vf${p.folder.id}`,
                    p.folder.name,
                    p.messages,
                    folderUnread.get(p.folder.id) ?? [],
                    p.folder.color,
                  ),
                )}
              </>
            )}
          </div>
        );
      })}
      {deleteMessage.error && (
        <div className="flex items-center gap-2 px-4 py-2">
          <p className="min-w-0 flex-1 text-[12px] text-red-700">
            Delete failed: {deleteMessage.error.message}
          </p>
          {reconnect &&
            (reconnect.url ? (
              <a
                href={reconnect.url}
                title={`Reconnect ${reconnect.username}`}
                className="shrink-0 rounded bg-accent px-3 py-1 text-[12px] font-medium text-white hover:bg-accent-hover"
              >
                Reconnect
              </a>
            ) : (
              <span className="shrink-0 text-[12px] text-ink-muted">Re-add via ＋</span>
            ))}
        </div>
      )}
      {hasNextPage && (
        <button
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mx-3 my-2 rounded border border-stroke px-3 py-1.5 text-[13px] text-accent hover:bg-accent-subtle disabled:opacity-50"
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
      {pendingDelete && (
        <ConfirmDialog
          title="Delete email?"
          message={`"${pendingDelete.subject}" from ${
            pendingDelete.from?.name ?? "unknown sender"
          } will be moved to Trash.`}
          onConfirm={runDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
